import {
    Box,
    Button,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { createSession, generateJWTToken } from '../../../../slices/userSessionSlice';
import { dashboardLink, loginErrorLink } from 'src/router/routerLink';
import { defaultError, otpInvalidError, parseErrorJson } from './helper';

import { Auth } from '@aws-amplify/auth';
import { LoadingButton } from '@mui/lab';
import Notification from './Notification';
import PropTypes from "prop-types";
import { ResendEmailIntervalSeconds } from 'src/appsettings';
import { getUserProfile } from '../../../../slices/settingSlice';
import log from 'loglevel';
import { messagingChannelActions } from 'src/slices/messagingChannelSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const OtpConfirm = ({ step, setStep, user, setUser, email, customNoti, setCustomNoti }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const initalSecondRemaining = ResendEmailIntervalSeconds;
    const [code, setCode] = useState('');
    const [statusCode, setStatusCode] = useState('');
    const [disableResendBtn, setDisableResendBtn] = useState(true);
    const [secondsRemaining, setSecondsRemaining] = useState(initalSecondRemaining);
    const [isVerifying, setIsVerifying] = useState(false);
    const disableLogin = statusCode == 20;
    const showTimer = secondsRemaining > 0;

    useEffect(() => {
        if (step !== 2) return;
        if (showTimer) {
            setDisableResendBtn(true);
        } else {
            setDisableResendBtn(false);
        }

        const timer = setTimeout(() => {
            if (secondsRemaining > 0) {
                setSecondsRemaining(prevSeconds => prevSeconds - 1);
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [step, secondsRemaining, showTimer]);

    const handleGoBackLogin = async () => {
        setStep(0);
        setCustomNoti({});
        navigate('/');
    };

    const subscribeMessage = (userId) => {
        dispatch(generateJWTToken())
            .unwrap()
            .then((res) => {

                dispatch(messagingChannelActions.startConnecting(res));
                dispatch(messagingChannelActions.subscribeRecordingStatus({ userId }));
                dispatch(messagingChannelActions.subscribeArchiveStatus({ userId }));
                dispatch(messagingChannelActions.subscribeDownloadTrimStatus({ userId }));
                dispatch(messagingChannelActions.subscribeExportCSVStatus({ userId }));
                dispatch(messagingChannelActions.subscribeSessionStatus({ userId }));

                log.info("subscribed signalr with user id ", userId);
            });
    }


    const verifyCode = async () => {
        setIsVerifying(true);
        setCustomNoti({});
        try {
            const answerUserResp = await Auth.sendCustomChallengeAnswer(user, code);
            setIsVerifying(false);
            console.log(`answerUserResp: ${JSON.stringify(answerUserResp)}`);

            const session = await Auth.currentSession();
            const validUser = session.isValid();
            if (validUser) {
                if (!sessionStorage.getItem("IsAuthenticated")) {
                    sessionStorage.setItem("IsAuthenticated", "true");
                }

                if (!sessionStorage.getItem('SettingProfile')) {
                    try {
                        await dispatch(createSession()).unwrap();
                        const res = await dispatch(getUserProfile()).unwrap();
                        sessionStorage.setItem('SettingAgencies', JSON.stringify(res.agencies));
                        sessionStorage.setItem('SettingAgenciesWithEndpoints', JSON.stringify(res.agencies_with_endpoints));
                        sessionStorage.setItem('SettingProfile', JSON.stringify(res.profile));
                        sessionStorage.setItem('SettingSuperAdminRole', JSON.stringify(res.super_admin_role));
                        sessionStorage.setItem('SuperAdminEmailDomains', JSON.stringify(res.superAdminEmailDomains));
                        sessionStorage.setItem('MaxAclDuration', JSON.stringify(res.maxAclDuration));
                        sessionStorage.setItem('MaxArchivedDuration', JSON.stringify(res.maxArchivedDuration));
                        sessionStorage.setItem('MaxArchivedExportDuration', JSON.stringify(res.maxArchivedExportDuration)); 
                        sessionStorage.setItem("CrossAgencies", JSON.stringify(res.cross_agencies));                        
                        const userId = res.profile.id;
                        subscribeMessage(userId);
                        navigate(dashboardLink);
                        console.log("Navigate to dashboard");
                    } catch (error) {
                        console.error('Error fetching user profile:', error);
                    }
                }
            }
            else {
                setIsVerifying(false);
                navigate(loginErrorLink);
            }
        } catch (error) {
            setIsVerifying(false);
            const customError = parseErrorJson(error.message);
            if (customError && customError?.code > 0) {

                setStatusCode(customError.code);
                if (customError.code === 10) {
                    navigate(loginErrorLink);
                }
                else if (customError.title && customError.message) {
                    setCustomNoti({ code: customError.code, status: 'error', title: customError.title, message: customError.message });
                }
            }
            else {
                setCustomNoti({ status: 'error', title: otpInvalidError.title, message: otpInvalidError.message });
            }

        }
    };

    const resendCode = async () => {
        setCode('');
        try {
            const resendUser = await Auth.signIn(email);
            setUser(resendUser);
            setCustomNoti({ status: 'success', title: "A One-Time Password has been sent", message: "You will receive an email with the OTP if you are registered." });
            setStatusCode(0);
            setSecondsRemaining(initalSecondRemaining);
        } catch (error) {
            setStatusCode(0);
            setCustomNoti({ status: 'error', title: defaultError.title, message: defaultError.message });
        }
    };

    const twoDigits = (num) => num < 10 ? `0${num}` : num;

    const formatTime = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        console.log()
        return `${twoDigits(minutes)}:${twoDigits(seconds)}`;
    };


    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column'
            }}
        >

            <Box sx={{ minHeight: '43px' }}>
                {customNoti.title && customNoti.message ? (
                    <Notification noti={customNoti} />
                ) : null}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '15px'
                }}
            >

                <Typography
                    sx={{
                        fontSize: '14px',
                        color: 'white'
                    }}
                >
                    One-Time Password*
                </Typography>
                <TextField
                    placeholder="Enter One-Time Password"
                    variant="outlined"
                    size="small"
                    required
                    value={code}
                    type='number'
                    onChange={(e) => {
                        setCode(e.target.value);
                    }}
                    sx={{
                        marginTop: '5px',
                        width: '359px',
                        fontFamily: 'Lato',
                        fontSize: '12px',
                        lineHeight: '16px',
                        background: '#1E243E',
                        letterSpacing: '0.32px',
                        '& input[type=number]': {
                            '-moz-appearance': 'textfield',
                        },
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            '-webkit-appearance': 'none',
                            margin: 0,
                        },
                        'input': {
                            '&::placeholder': {
                                fontStyle: 'italic',
                                textAlign: 'left',
                                fontFamily: 'Lato',
                                lineHeight: '16px',
                                fontWeight: 400,
                                color: '#B2B7C2'
                            }
                        },
                        "& .MuiOutlinedInput-root": {
                            "& > fieldset": { borderRadius: 0 },
                        },
                    }}
                    inputProps={{
                        inputMode: 'numeric'
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginTop: '5px'
                    }}
                >
                    <Button
                        sx={{
                            justifyContent: 'left'
                        }}
                        disabled={disableResendBtn}
                        onClick={resendCode}
                        color="primary"
                        size="small"
                        variant="contained"
                    >
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: disableResendBtn ? '#B2B7C2' : '#84FFFF',

                            }}
                        >
                            Resend OTP
                        </Typography>
                    </Button>

                    {showTimer &&
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: '#84FFFF',

                            }}
                        >
                            ({formatTime(secondsRemaining)})
                        </Typography>
                    }
                </Box>




                <LoadingButton
                    sx={{
                        marginTop: '10px',
                        justifyContent: 'center',
                        width: '359px',
                        height: '40px',
                        background: '#6378CA',
                        borderRadius: '2px',
                        '&:hover': {
                            background: '#3D58BD'
                        }
                    }}
                    loading={isVerifying}
                    disabled={disableLogin}
                    onClick={verifyCode}
                    color="primary"
                    size="large"
                    variant="contained"
                >
                    <Typography
                        sx={{
                            whiteSpace: 'nowrap',
                            flexWrap: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'center',
                            height: '26px',
                            fontWeight: '200',
                            fontSize: '14px',
                            lineHeight: '26px',
                            color: '#FFFFFF'
                        }}
                    >
                        Log in
                    </Typography>
                </LoadingButton>

                <Typography
                    onClick={handleGoBackLogin}
                    sx={{
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 200,
                        color: '#84FFFF',
                        marginTop: '10px',
                        cursor: 'pointer',
                        '&:hover': { color: '#3D58BD', textDecoration: 'underline' },
                        textDecoration: 'underline',
                    }}
                >
                    Or login with a different account?
                </Typography>
            </Box>
        </Box>
    )
}

OtpConfirm.propTypes = {
    step: PropTypes.number,
    setStep: PropTypes.func,
    user: PropTypes.object.isRequired,
    setUser: PropTypes.func,
    email: PropTypes.string.isRequired,
    customNoti: PropTypes.object,
    setCustomNoti: PropTypes.func
};
export default OtpConfirm;